import React from 'react';
import { Image, Text, View, StyleSheet } from '@react-pdf/renderer';

type TableHeaders<T> = {
  title: string;
  value: keyof T;
  render?: (item: any, rowData?: T, index?: number) => JSX.Element;
};

interface PDFTableProps<T> {
  headerData: Record<string, any>;
  data?: T[] | null;
  tableHeaders: TableHeaders<T>[];
  extraStyle?: boolean;
}

const PDFTable = <T extends Record<string, any>>({
  headerData,
  data = [],
  tableHeaders,
  extraStyle = false,
}: PDFTableProps<T>) => {
  const styles = StyleSheet.create({
    table: {
      width: '100%',
      borderRadius: 4,
      padding: 24,
      border: extraStyle ? '' : '1px solid #e0e0e0',
    },
    row: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#e0e0e0',
      alignItems: 'center',
      minHeight: 30,
    },
    rowHead: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    cell: {
      flex: 1,
      padding: 8,
      fontSize: 11,
      color: '#424242',
      textTransform: 'capitalize',
    },
    headerCell: {
      backgroundColor: '#F9E9FF',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
    },
    snCell: {
      flex: 0.4,
      padding: 8,
      fontSize: 11,
      color: '#424242',
      backgroundColor: '#F9E9FF',
      fontWeight: 'bold',
      textAlign: 'center',
    },
    innerHeading: {
      fontSize: 16,
      fontWeight: 'bold',
      marginVertical: 12,
    },
    // Header Styles
    header: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      position: 'relative',
      padding: '10px',
      height: '100px',
    },
    logo: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      width: 'auto',
      height: '60px',
    },
    info: {
      textAlign: 'left',
      width: '100%',
    },
    documentType: {
      fontSize: '15px',
      fontWeight: 'bold',
    },
    text: {
      fontSize: '13px',
      marginTop: '5px',
    },
  });
  const renderCellContent = (header: TableHeaders<T>, item: T, index: number) =>
    header.render
      ? header.render(item[header.value], item, index)
      : item[header.value] ?? '';

  return (
    <View wrap>
      <section style={styles.header}>
        <Image style={styles.logo} src="/logos/logo-purple.png" />
        <div style={styles.info}>
          {Object.keys(headerData).map((key) => (
            <Text key={key} style={styles.text}>
              {key}: {headerData[key]}
            </Text>
          ))}
        </div>
      </section>
      <View style={styles.table}>
        {/* Table Headers */}
        <View style={styles.rowHead}>
          <Text style={styles.snCell}>S.N</Text>
          {tableHeaders.map((thead) => (
            <Text key={thead.title} style={[styles.cell, styles.headerCell]}>
              {thead.title}
            </Text>
          ))}
        </View>

        {/* Table Content */}
        {data.map((item, dataIndex) => (
          <View key={dataIndex} style={styles.row}>
            <Text style={styles.snCell}>{dataIndex + 1}.</Text>
            {tableHeaders.map((header, headerIndex) => (
              <Text key={headerIndex} style={styles.cell}>
                {renderCellContent(header, item, dataIndex)}
              </Text>
            ))}
          </View>
        ))}
      </View>
    </View>
  );
};

export default PDFTable;
